import React, { useState, useEffect, useCallback } from 'react';
import { adminCoupons } from '../../utils/api';
import toast from 'react-hot-toast';

const emptyCoupon = {
  code: '', type: 'percentage', value: '',
  min_order_amount: 0, max_uses: '', expires_at: '', is_active: 1,
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const toDateInput = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date)) return '';
  return date.toISOString().slice(0, 10);
};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCoupon);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminCoupons.getAll()
      .then(setCoupons)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyCoupon); setEditing(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      ...emptyCoupon,
      ...c,
      expires_at: toDateInput(c.expires_at),
      max_uses: c.max_uses ?? '',
    });
    setEditing(c.id);
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value),
        min_order_amount: parseFloat(form.min_order_amount || 0),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      if (editing) {
        await adminCoupons.update(editing, payload);
        toast.success('Coupon updated');
      } else {
        await adminCoupons.create(payload);
        toast.success('Coupon created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"? This cannot be undone.`)) return;
    try {
      await adminCoupons.delete(c.id);
      toast.success('Coupon deleted');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatValue = (c) => c.type === 'percentage' ? `${c.value}%` : `KD ${parseFloat(c.value).toFixed(3)}`;

  return (
    <div>
      <div className="admin-page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'1.5rem' }}>
          Coupons <span style={{ fontSize:'1rem', color:'var(--text-muted)', fontFamily:'var(--font-sans)' }}>({coupons.length})</span>
        </h2>
        <button className="btn btn-primary" onClick={openNew}>+ Add Coupon</button>
      </div>

      <div className="admin-card">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Value</th>
                <th>Min Order</th>
                <th>Used / Limit</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>
              ) : coupons.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No coupons yet. Click "Add Coupon" to create one.</td></tr>
              ) : coupons.map(c => (
                <tr key={c.id}>
                  <td><strong style={{ fontFamily:'monospace', fontSize:'0.88rem' }}>{c.code}</strong></td>
                  <td style={{ fontSize:'0.82rem', textTransform:'capitalize' }}>{c.type}</td>
                  <td><strong>{formatValue(c)}</strong></td>
                  <td>KD {parseFloat(c.min_order_amount || 0).toFixed(3)}</td>
                  <td>{c.used_count || 0} / {c.max_uses ? c.max_uses : '∞'}</td>
                  <td style={{ fontSize:'0.82rem' }}>{fmtDate(c.expires_at)}</td>
                  <td>
                    {c.is_active
                      ? <span className="badge badge-olive">Active</span>
                      : <span className="badge badge-red">Inactive</span>}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="action-btn" onClick={() => openEdit(c)}>Edit</button>
                      <button className="action-btn action-btn--danger" onClick={() => handleDelete(c)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal modal--lg">
            <div className="modal-header">
              <h3>{editing ? 'Edit Coupon' : 'New Coupon'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Code *</label>
                  <input
                    name="code"
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    className="form-control"
                    style={{ fontFamily:'monospace', textTransform:'uppercase' }}
                    placeholder="SAVE20"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select name="type" value={form.type} onChange={handleChange} className="form-control" required>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (KD)</option>
                  </select>
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Value *</label>
                  <input
                    name="value" type="number" step="0.001" min="0"
                    value={form.value} onChange={handleChange}
                    className="form-control"
                    placeholder={form.type === 'percentage' ? '20' : '5.000'}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Minimum Order (KD)</label>
                  <input
                    name="min_order_amount" type="number" step="0.001" min="0"
                    value={form.min_order_amount} onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Max Uses (blank = unlimited)</label>
                  <input
                    name="max_uses" type="number" min="0"
                    value={form.max_uses} onChange={handleChange}
                    className="form-control"
                    placeholder="100"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expires On (blank = never)</label>
                  <input
                    name="expires_at" type="date"
                    value={form.expires_at || ''} onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={handleChange} />
                  Active
                </label>
              </div>

              <div style={{ display:'flex', gap:12, marginTop:20 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editing ? 'Update Coupon' : 'Create Coupon')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); }
        .admin-table-wrap { overflow-x: auto; }
        .admin-table { width: 100%; border-collapse: collapse; }
        .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); border-bottom: 2px solid var(--border); }
        .admin-table td { padding: 12px; font-size: 0.85rem; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .admin-table tr:hover td { background: #fafafa; }
        .action-btn { padding: 6px 14px; background: var(--cream); border-radius: 6px; font-size: 0.78rem; font-weight: 600; font-family: var(--font-sans); transition: all 0.2s; }
        .action-btn:hover { background: var(--gold); color: white; }
        .action-btn--danger:hover { background: #e53e3e; color: white; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal { background: white; border-radius: var(--radius-lg); width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; }
        .modal--lg { max-width: 680px; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid var(--border); position: sticky; top: 0; background: white; z-index: 1; }
        .modal-header h3 { font-family: var(--font-serif); }
        .modal-body { padding: 24px; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .form-checkboxes { display: flex; gap: 20px; flex-wrap: wrap; }
        .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; font-weight: 500; cursor: pointer; }
        @media (max-width: 480px) { .form-grid-2 { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default AdminCoupons;
